import React, { useState, useEffect } from 'react';

import {
  useFloating,
  offset,
  shift,
  autoUpdate,
  useDismiss,
  useClick,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import { CrossIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import st from './Popover.module.sass';

export function Popover({
  trigger,
  portalTarget,
  children,
  placementProp = 'left-start',
  hasError = false,
  disabled = false,
  className = undefined,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (hasError) setIsOpen(true);
  }, [hasError]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(12), shift({ padding: 8 })],
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    placement: placementProp,
  });

  const click = useClick(context, { enabled: false });
  const isCalendarOpen = () => document.querySelector('.flatpickr-calendar.open');
  const dismiss = useDismiss(context, {
    outsidePress: () => !isCalendarOpen() && !hasError,
    escapeKey: true,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const toggleOpen = () => !hasError && !disabled && setIsOpen((prev) => !prev);
  const closePopover = () => {
    if (hasError) {
      toast.error(t('error.invalid form data'));
      return;
    }
    setIsOpen(false);
  };

  const enhancedTrigger = React.isValidElement(trigger)
    ? React.cloneElement(trigger, {
      className: { [st.isOpen]: isOpen },
    })
    : trigger;

  return (
    <React.Fragment>
      <span
        onClick={toggleOpen}
        onKeyDown={toggleOpen}
        ref={refs.setReference}
        {...getReferenceProps()}
        role="button"
        tabIndex={-1}
      >
        {enhancedTrigger}
      </span>

      {!!isOpen && createPortal(
        <div
          className={cn(st.Popover, className)}
          data-floating-popover=""
          data-testid="popoverRoot"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          role="dialog"
        >
          <Control.IconButton
            className={st.CloseButton}
            data-testid="popover.closeButton"
            icon={<CrossIcon />}
            kind="secondary"
            onClick={closePopover}
            variant="dimmed"
          />
          {children}
        </div>,
        portalTarget,
      )}
    </React.Fragment>
  );
}

Popover.propTypes = {
  trigger: PropTypes.node.isRequired,
  portalTarget: PropTypes.instanceOf(HTMLElement).isRequired,
  children: PropTypes.node.isRequired,
  placementProp: PropTypes.oneOf(['top', 'bottom', 'left', 'right',
    'top-start', 'top-end', 'bottom-start', 'bottom-end',
    'left-start', 'left-end', 'right-start', 'right-end']),
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};
